/** Formatting helpers. Numbers a person reads, not raw units. */

export function bytes(value: number): string {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
  const scaled = value / 1024 ** index;
  return `${scaled >= 100 || index === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[index]}`;
}

export function duration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((part) => String(part).padStart(2, "0")).join(":");
}

/** Elapsed seconds from an ISO timestamp to ``nowMs`` (defaults to Date.now()). */
export function since(iso: string | null, nowMs: number = Date.now()): number {
  if (!iso) return 0;
  const start = new Date(iso).getTime();
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, (nowMs - start) / 1000);
}

/** Prefer started_at; fall back to first frame / queue time for live clocks. */
export function runClockStart(
  run: {
    started_at?: string | null;
    first_frame_at?: string | null;
    requested_start_at?: string | null;
  },
): string | null {
  return run.started_at || run.first_frame_at || run.requested_start_at || null;
}

/** Live or final duration for a run. Prefer the frozen DB value once stopped. */
export function runDuration(
  run: {
    started_at: string | null;
    stopped_at?: string | null;
    duration_seconds?: number;
    first_frame_at?: string | null;
    requested_start_at?: string | null;
  },
  nowMs: number = Date.now(),
): number {
  const start = runClockStart(run);
  if (run.stopped_at) {
    if (run.duration_seconds && run.duration_seconds > 0) return run.duration_seconds;
    if (start) return since(start, new Date(run.stopped_at).getTime());
    return 0;
  }
  return since(start, nowMs);
}

export function relative(iso: string | null): string {
  if (!iso) return "—";
  const seconds = since(iso);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function clockTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour12: false });
}

export function number(value: number | undefined | null, digits = 0): string {
  if (value === undefined || value === null) return "—";
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
}
